import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, UserX, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEmployerBrand, BrandLogo, PoweredByBadge } from "@/lib/brand";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawApplication() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const { brand } = useEmployerBrand(info?.employer_id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/public/withdraw/${token}/info`
        );
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.data?.detail ||
              "We couldn't verify this link. It may have expired or already been used."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API_BASE}/api/public/withdraw/${token}/confirm`);
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Withdrawal failed.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <main
      data-testid="withdraw-page"
      className="min-h-screen bg-slate-50 px-6 py-12"
    >
      <div className="mx-auto max-w-xl rounded-sm border border-slate-200 bg-white p-8 shadow-sm">
        {brand?.logo_url && (
          <div className="mb-6 flex justify-center">
            <BrandLogo brand={brand} className="h-12 w-auto max-w-[180px] object-contain" />
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-3 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Verifying link…
          </div>
        )}

        {error && !loading && (
          <div data-testid="withdraw-error">
            <div className="mb-3 flex items-center gap-3 text-amber-700">
              <AlertTriangle className="h-6 w-6" />
              <h1 className="text-xl font-bold">Link unavailable</h1>
            </div>
            <p className="text-sm text-slate-600">{error}</p>
            <Link
              to="/application-status"
              className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-900"
            >
              Check application status <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {info && !done && !error && (
          <div data-testid="withdraw-confirm">
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-rose-100 text-rose-700">
              <UserX className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {info.already_withdrawn ? "Already withdrawn" : "Withdraw your application"}
            </h1>
            {info.already_withdrawn ? (
              <p className="mt-2 text-sm text-slate-600">
                This application is already in the <b>Not Interested</b> stage. No further action needed.
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm text-slate-600">
                  We'll mark your application for <b>{info.position || "this role"}</b>{" "}
                  (<span className="font-mono">{info.application_ref}</span>) as withdrawn. You won't receive any more reminders.
                </p>
                <p className="mt-4 text-sm text-slate-700">
                  Changed your mind? Just close this page — nothing happens unless you click confirm.
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <Button
                    data-testid="withdraw-confirm-btn"
                    onClick={confirm}
                    disabled={confirming}
                    className="bg-rose-600 text-white hover:bg-rose-500"
                  >
                    {confirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Yes — withdraw my application
                  </Button>
                  <Link to="/application-status" className="text-sm text-slate-500 hover:text-slate-800">
                    Keep my application active
                  </Link>
                </div>
              </>
            )}
          </div>
        )}

        {done && (
          <div data-testid="withdraw-success">
            <div className="mb-3 flex items-center gap-3 text-emerald-700">
              <CheckCircle2 className="h-6 w-6" />
              <h1 className="text-xl font-bold">Application withdrawn</h1>
            </div>
            <p className="text-sm text-slate-700">
              Your application for <b>{info.position || "this role"}</b> has been moved to the
              Not Interested stage. Thanks for letting us know.
            </p>
          </div>
        )}
      </div>
      <div className="mx-auto mt-6 flex max-w-xl justify-end">
        <PoweredByBadge brand={brand} />
      </div>
    </main>
  );
}
